import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";

import { api, useAlCambiar, useDatos } from "../api";
import { useAccion } from "../componentes/acciones";
import { Carga } from "../componentes/Carga";
import { DetallePipeline } from "../componentes/DetallePipeline";
import { DialogoResultado, RESULTADOS } from "../componentes/Dialogos";
import { Badge, Button, Chip, EmptyState, IconButton, Input, Menu, Page, SegmentedControl, Select, TopBar, iniciales } from "../ds";
import { destinos, fase as datosFase, pideConfirmacion, puedeMover, siguiente } from "../fases";
import { fechaHora, hace, nombrePlantilla, numero, usd } from "../formato";
import { Enlace, navegar, useUbicacion } from "../router";
import type { AccionPipeline, Fase, Pipeline as DatosPipeline, Resultado, TarjetaPipeline } from "../tipos";
import s from "./Pipeline.module.css";

const FILTROS = ["q", "plantilla", "zona", "responsable", "accion", "dias", "esperando"] as const;

export function Pipeline() {
  const { params } = useUbicacion();
  const consulta = useMemo(() => {
    const p = new URLSearchParams();
    for (const k of FILTROS) {
      const v = params.get(k);
      if (v) p.set(k, v);
    }
    return p.toString();
  }, [params]);
  const { datos, error, cargando, recargar } = useDatos<DatosPipeline>(`/api/pipeline${consulta ? `?${consulta}` : ""}`);
  useAlCambiar(recargar);
  const { ejecutar } = useAccion();

  const [texto, setTexto] = useState(params.get("q") ?? "");
  const [abierta, setAbierta] = useState<string | null>(null);
  const [dialogo, setDialogo] = useState<{ inmobiliaria: { id: string; nombre: string }; resultado: Resultado } | null>(null);
  const [arrastrada, setArrastrada] = useState<TarjetaPipeline | null>(null);
  const [destino, setDestino] = useState<Fase | null>(null);
  const [faseElegida, setFaseMovil] = useState<Fase | null>(null);
  const [verNoInteresa, setVerNoInteresa] = useState(false);
  const [filtrosMovil, setFiltrosMovil] = useState(false);

  const poner = (cambios: Partial<Record<(typeof FILTROS)[number], string | null>>) => {
    const p = new URLSearchParams(consulta);
    for (const [k, v] of Object.entries(cambios)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    const q = p.toString();
    navegar(q ? `/pipeline?${q}` : "/pipeline", { reemplazar: true });
  };

  // La búsqueda espera a que se deje de tipear.
  useEffect(() => {
    if (texto === (params.get("q") ?? "")) return;
    const t = setTimeout(() => poner({ q: texto.trim() || null }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [texto]);

  // La tarjeta abierta se sigue mostrando aunque al moverla quede fuera del filtro.
  const ultimaAbierta = useRef<TarjetaPipeline | null>(null);
  const tarjeta = datos?.columnas.flatMap((c) => c.tarjetas).find((t) => t.id === abierta) ?? (abierta ? ultimaAbierta.current : null);
  ultimaAbierta.current = tarjeta?.id === abierta ? tarjeta : null;

  const mover = (t: TarjetaPipeline, hacia: Fase) => {
    const resultado = datosFase(hacia).resultado;
    if (!resultado || !puedeMover(t.fase, hacia)) return;
    if (pideConfirmacion(hacia)) {
      setDialogo({ inmobiliaria: { id: t.id, nombre: t.nombre }, resultado });
      return;
    }
    const etiqueta = RESULTADOS.find((r) => r.valor === resultado)?.etiqueta ?? datosFase(hacia).etiqueta;
    void ejecutar(`mover-${t.id}`, () => api(`/api/leads/${t.id}/resultado`, { metodo: "POST", cuerpo: { resultado } }), () => ({
      title: `${t.nombre}: ${etiqueta.toLowerCase()}`,
      message: "Si fue un error, se deshace desde el historial.",
    }));
  };

  // ↑ ↓ con el detalle abierto: la tarjeta anterior o siguiente de la misma columna.
  const navegarEnColumna = (paso: 1 | -1) => {
    const col = datos?.columnas.find((c) => c.fase === tarjeta?.fase);
    const i = col?.tarjetas.findIndex((t) => t.id === tarjeta?.id) ?? -1;
    const otra = col && i >= 0 ? col.tarjetas[i + paso] : undefined;
    if (otra) setAbierta(otra.id);
  };

  const finArrastre = () => {
    setArrastrada(null);
    setDestino(null);
  };

  // En celular se ve una fase por vez: hasta que se elija otra, la primera con algo que hacer.
  const faseMovil: Fase =
    faseElegida ??
    datos?.columnas.find((c) => c.tarjetas.some((t) => t.accion))?.fase ??
    datos?.columnas.find((c) => c.fase !== "esperando" && c.total > 0)?.fase ??
    "esperando";

  const hayFiltros = ["q", "plantilla", "zona", "responsable", "accion", "dias"].some((k) => params.get(k));
  const cantidadFiltros = ["plantilla", "zona", "responsable", "accion", "dias"].filter((k) => params.get(k)).length;
  const op = datos?.opciones;

  return (
    <>
      <TopBar
        title="Pipeline"
        subtitle="Cada inmobiliaria contactada, en la fase en que quedó. Las respuestas por Chatwoot se mueven solas."
        actions={
          datos ? (
            <>
              {datos.necesitanAccion ? (
                <Badge tone="warning" size="md" dot>
                  {numero(datos.necesitanAccion)} necesitan acción
                </Badge>
              ) : null}
              <Badge tone="neutral" size="md">
                {numero(datos.total)} en el pipeline
              </Badge>
            </>
          ) : null
        }
      />
      <Page width="none" gap="md">
        <div className={s.filtros} data-abiertos={filtrosMovil}>
          <Input
            aria-label="Buscar"
            icon="search"
            type="search"
            placeholder="Buscar inmobiliaria o teléfono"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            className={s.busqueda}
          />
          <Button size="sm" variant={cantidadFiltros ? "soft" : "outline"} icon="filter" className={s.botonFiltros} onClick={() => setFiltrosMovil((v) => !v)} aria-expanded={filtrosMovil}>
            Filtros{cantidadFiltros ? ` ${cantidadFiltros}` : ""}
          </Button>
          <div className={s.resto}>
            <Chip selected={params.get("accion") === "1"} icon="alert-triangle" onClick={() => poner({ accion: params.get("accion") === "1" ? null : "1" })}>
              Necesitan acción{datos ? ` ${numero(params.get("accion") === "1" ? datos.total : datos.necesitanAccion)}` : ""}
            </Chip>
            <Select
              variant="filter"
              aria-label="Plantilla"
              placeholder="Plantilla"
              value={params.get("plantilla") ?? ""}
              onChange={(e) => poner({ plantilla: e.target.value || null })}
              menuMinWidth={240}
              options={[
                { value: "", label: "Todas las plantillas" },
                ...(op?.plantillas ?? []).map((p) => ({ value: p, label: p })),
                { value: "sin_plantilla", label: "Sin plantilla" },
              ]}
            />
            <Select
              variant="filter"
              aria-label="Zona"
              placeholder="Zona"
              value={params.get("zona") ?? ""}
              onChange={(e) => poner({ zona: e.target.value || null })}
              menuMinWidth={220}
              options={[{ value: "", label: "Todas las zonas" }, ...(op?.zonas ?? []).map((z) => ({ value: z, label: z }))]}
            />
            <Select
              variant="filter"
              aria-label="Responsable"
              placeholder="Responsable"
              value={params.get("responsable") ?? ""}
              onChange={(e) => poner({ responsable: e.target.value || null })}
              options={[{ value: "", label: "Todos" }, ...(op?.responsables ?? []).map((r) => ({ value: r.usuario, label: r.nombre }))]}
            />
            <SegmentedControl
              label="Enviados"
              size="sm"
              value={params.get("dias") ?? "todo"}
              onChange={(v) => poner({ dias: v === "todo" ? null : v })}
              options={[
                { value: "7", label: "7 días" },
                { value: "30", label: "30 días" },
                { value: "todo", label: "Todo" },
              ]}
            />
            {hayFiltros ? (
              <Button size="sm" variant="ghost" icon="x" onClick={() => (setTexto(""), poner({ q: null, plantilla: null, zona: null, responsable: null, accion: null, dias: null }))}>
                Limpiar
              </Button>
            ) : null}
          </div>
        </div>

        {!datos ? (
          <Carga error={error} onReintentar={recargar} filas={6} />
        ) : datos.total === 0 && !hayFiltros ? (
          <EmptyState
            icon="kanban"
            title="Todavía no salió ningún primer WhatsApp"
            tone="dashed"
            action={
              <Button variant="soft" icon="whatsapp" as={Enlace} href="/cola">
                Ir a la cola
              </Button>
            }
          >
            Cada inmobiliaria a la que le mandes la apertura aparece acá, y avanza de columna a medida que responde.
          </EmptyState>
        ) : (
          <>
            <div className={s.fasesMovil} role="tablist" aria-label="Fase">
              {datos.columnas.map((c) => (
                <Chip key={c.fase} role="button" size="sm" selected={faseMovil === c.fase} onClick={() => setFaseMovil(c.fase)}>
                  {datosFase(c.fase).corta} {numero(c.total)}
                </Chip>
              ))}
            </div>
            <div className={s.tablero} aria-busy={cargando}>
              {datos.columnas.map((c) => {
                const info = datosFase(c.fase);
                const colapsada = c.fase === "no_interesa" && !verNoInteresa && faseMovil !== "no_interesa";
                const valido = arrastrada ? puedeMover(arrastrada.fase, c.fase) : false;
                return (
                  <section
                    key={c.fase}
                    className={s.columna}
                    aria-label={`${info.etiqueta}: ${c.total}`}
                    data-fase={c.fase}
                    data-activa={faseMovil === c.fase}
                    data-colapsada={colapsada}
                    data-destino={valido && destino === c.fase}
                    data-apagada={Boolean(arrastrada) && !valido && arrastrada?.fase !== c.fase}
                    onDragOver={(e) => {
                      if (!valido) return;
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                      if (destino !== c.fase) setDestino(c.fase);
                    }}
                    onDragLeave={(e) => {
                      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDestino((d) => (d === c.fase ? null : d));
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (arrastrada && valido) mover(arrastrada, c.fase);
                      finArrastre();
                    }}
                  >
                    {colapsada ? (
                      <button type="button" className={s.colapsada} onClick={() => setVerNoInteresa(true)} title="Ver las que no les interesa">
                        <span className={s.total}>{numero(c.total)}</span>
                        <span className={s.vertical}>{info.etiqueta}</span>
                      </button>
                    ) : (
                      <>
                        <header className={s.cabecera}>
                          <span className={s.titulo}>{info.etiqueta}</span>
                          <span className={s.total}>{numero(c.total)}</span>
                          {c.fase === "no_interesa" && faseMovil !== "no_interesa" ? (
                            <IconButton icon="chevron-right" label="Ocultar" size="sm" variant="ghost" onClick={() => setVerNoInteresa(false)} />
                          ) : null}
                        </header>
                        {valido && destino === c.fase ? <div className={s.soltar}>Soltar para marcar {info.etiqueta}</div> : null}
                        {c.tarjetas.map((t) => (
                          <Tarjeta
                            key={t.id}
                            t={t}
                            seleccionada={t.id === abierta}
                            arrastrando={arrastrada?.id === t.id}
                            onAbrir={() => setAbierta(t.id)}
                            onMover={(f) => mover(t, f)}
                            onArrastrar={() => setArrastrada(t)}
                            onFinArrastre={finArrastre}
                          />
                        ))}
                        {c.tarjetas.length === 0 ? <p className={s.vacia}>{hayFiltros ? "Nada con estos filtros." : VACIAS[c.fase]}</p> : null}
                        {c.fase === "esperando" && c.total > c.tarjetas.length ? (
                          <Button size="sm" variant="ghost" onClick={() => poner({ esperando: "todas" })}>
                            Ver las {numero(c.total - c.tarjetas.length)} restantes
                          </Button>
                        ) : null}
                      </>
                    )}
                  </section>
                );
              })}
            </div>
          </>
        )}
      </Page>
      <DetallePipeline
        tarjeta={tarjeta}
        onCerrar={() => setAbierta(null)}
        onMover={(f) => tarjeta && mover(tarjeta, f)}
        onNavegar={navegarEnColumna}
        dialogoAbierto={Boolean(dialogo)}
      />
      <DialogoResultado inmobiliaria={dialogo?.inmobiliaria ?? null} resultado={dialogo?.resultado ?? null} onCerrar={() => setDialogo(null)} onListo={recargar} />
    </>
  );
}

const VACIAS: Record<Fase, string> = {
  esperando: "Nadie esperando respuesta.",
  respondio: "Las respuestas de Chatwoot llegan acá solas.",
  info_enviada: "Arrastrá acá a quien le mandaste la info.",
  seguimiento: "Los que tienen la info y hay que volver a escribirles.",
  pidio_mas: "Los que piden más después de la info.",
  reunion: "Reuniones y demos coordinadas.",
  cliente: "Todavía ninguno.",
  no_interesa: "Nadie dijo que no.",
};

export function textoAccion(a: AccionPipeline): string {
  if (a.tipo === "sin_clasificar") return `Sin clasificar · ${a.dias} d`;
  if (a.tipo === "sin_respuesta") return `Sin respuesta · ${a.dias} d`;
  return `Fue el ${fechaHora(a.fecha)} · ¿cómo salió?`;
}

const ATAJO: Partial<Record<Fase, string>> = { respondio: "Respondió", info_enviada: "Info enviada", seguimiento: "Seguimiento", pidio_mas: "Le interesó", reunion: "Agendar", cliente: "Es cliente" };

function Tarjeta({
  t,
  seleccionada,
  arrastrando,
  onAbrir,
  onMover,
  onArrastrar,
  onFinArrastre,
}: {
  t: TarjetaPipeline;
  seleccionada: boolean;
  arrastrando: boolean;
  onAbrir: () => void;
  onMover: (f: Fase) => void;
  onArrastrar: () => void;
  onFinArrastre: () => void;
}) {
  const sig = siguiente(t.fase);
  const opciones = destinos(t);
  // Esperar es lo normal: ahí la acción rápida no suma, Chatwoot la mueve sola.
  const rapida = sig && t.fase !== "esperando" ? sig : null;
  const cita = t.fase === "respondio" || t.fase === "pidio_mas" ? t.respuesta : null;
  const linea =
    t.fase === "esperando"
      ? [t.zona, t.puntaje !== null ? `puntaje ${t.puntaje}` : null].filter(Boolean).join(" · ")
      : t.fase === "cliente"
        ? `Cerró el ${fechaHora(t.desde)}`
        : (t.nota ?? (cita ? null : t.zona));
  const parar = (e: { stopPropagation: () => void }) => e.stopPropagation();
  const teclado = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      onAbrir();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`${t.nombre}, ${datosFase(t.fase).etiqueta}`}
      className={s.tarjeta}
      data-seleccionada={seleccionada}
      data-accion={Boolean(t.accion)}
      data-nueva={t.nueva}
      data-arrastrando={arrastrando}
      draggable={opciones.length > 0}
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", t.id);
        e.dataTransfer.effectAllowed = "move";
        onArrastrar();
      }}
      onDragEnd={onFinArrastre}
      onClick={onAbrir}
      onKeyDown={teclado}
    >
      <div className={s.nombre}>
        <span>{t.nombre}</span>
        {t.nueva ? <span className={s.punto} aria-label="Respuesta nueva" /> : null}
      </div>
      {cita ? <p className={s.cita}>“{t.respuesta}”</p> : null}
      {t.fase === "reunion" && t.reunion && !t.accion ? <span className={s.fecha}>{fechaHora(t.reunion)}</span> : null}
      {t.fase === "cliente" ? <span className={s.cash}>{usd(t.cobradoUsd)}</span> : null}
      {t.demo && t.fase !== "cliente" && t.fase !== "no_interesa" ? (
        t.demo.visitas > 0 ? (
          <span className={s.demo} title={`Abrió su demo ${t.demo.visitas === 1 ? "una vez" : `${t.demo.visitas} veces`}`}>
            Abrió la demo · {hace(t.demo.abiertaEn)}
          </span>
        ) : (
          <span className={s.demoLista} title="Tiene la demo armada: el link está en el detalle">
            Demo lista
          </span>
        )
      ) : null}
      {linea ? <span className={s.linea}>{linea}</span> : null}
      {t.fase === "esperando" ? <span className={s.plantilla}>{nombrePlantilla(t.plantilla)}</span> : null}

      {t.accion?.tipo === "reunion_pasada" ? (
        <>
          <span className={s.alerta}>{textoAccion(t.accion)}</span>
          <div className={s.botones} onClick={parar}>
            <Button size="sm" variant="ink" onClick={() => onMover("cliente")}>
              Cliente
            </Button>
            <Button size="sm" variant="danger-soft" onClick={() => onMover("no_interesa")}>
              No
            </Button>
          </div>
        </>
      ) : (
        <div className={s.pie}>
          <span className={t.accion ? s.alerta : s.cuando}>{t.accion ? textoAccion(t.accion) : hace(t.fase === "esperando" ? t.enviadoEn : t.desde)}</span>
          {t.responsable ? (
            <span className={s.responsable} title={`Responsable: ${t.responsable}`}>
              {iniciales(t.responsable)}
            </span>
          ) : null}
        </div>
      )}

      {rapida && t.accion?.tipo !== "reunion_pasada" ? (
        <div className={s.acciones} onClick={parar}>
          <Button size="sm" variant="soft" iconAfter="arrow-right" onClick={() => onMover(rapida)} className={s.rapida}>
            {ATAJO[rapida]}
          </Button>
          {opciones.length > 1 ? (
            <Menu
              label="Mover a"
              align="right"
              trigger={<IconButton icon="ellipsis-vertical" label="Mover a otra fase" size="sm" variant="ghost" />}
              items={opciones.map((o) => ({ label: o.etiqueta, dot: o.fase === "no_interesa" ? "danger" : "success", onSelect: () => onMover(o.fase) }))}
            />
          ) : null}
        </div>
      ) : t.fase === "esperando" ? (
        <div className={`${s.acciones} ${s.soloMovil}`} onClick={parar}>
          <Menu
            label="Mover a"
            trigger={
              <Button size="sm" variant="outline" iconAfter="chevron-down">
                Mover a…
              </Button>
            }
            items={opciones.map((o) => ({ label: o.etiqueta, dot: o.fase === "no_interesa" ? "danger" : "success", onSelect: () => onMover(o.fase) }))}
          />
        </div>
      ) : null}
    </div>
  );
}
